import {
  ONBOARDING_IDENTITY_PROVIDED_KEYS, resolveTrustedContact, withLegacyEmployerTitle, type OnboardingClientFormSection,
} from '@steward-app/steward-types';

/**
 * How far along one section of the onboarding form is (crossmarc/steward-app#1765). Shared by
 * the client's left nav (a done, half or empty check) and the firm's Onboarding details tab
 * (Complete, In progress, Not started), so the two can never disagree about the same answers.
 *
 * A section is `complete` when the answers the firm needs to open accounts are there, not
 * when every box is filled: middle name, employer and the mailing lines when "same as legal"
 * is ticked are optional. "About you" is optional as a whole, so it is `complete` once
 * anything is in it and `empty` otherwise, never `partial`. Children (crossmarc/steward-app#1812)
 * is optional as a whole too, the same way: nobody is required to have children to open accounts.
 *
 * Takes {@link OnboardingClientFormSection}, not the wider `OnboardingFormSection`: CPA and
 * attorney were removed from the client-facing form in crossmarc/steward-app#1850 (RIPL's
 * signed-off mocks never drew them), though appcore's schema still declares both for existing
 * rows — see `packages/steward-types/src/onboardingForm.ts`'s module header.
 */
export type SectionStatus = 'complete' | 'partial' | 'empty';

type Answers = Record<string, unknown>;

const has = (a: Answers, k: string): boolean => {
  const v = a[k];
  if (Array.isArray(v)) return v.some((x) => typeof x === 'string' && x.trim() !== '');
  return typeof v === 'string' ? v.trim() !== '' : typeof v === 'boolean';
};

function fromRequired(a: Answers, required: string[], any: string[]): SectionStatus {
  if (required.every((k) => has(a, k))) return 'complete';
  return any.some((k) => has(a, k)) ? 'partial' : 'empty';
}

const address = (prefix: string) => ['Street', 'City', 'State', 'PostalCode'].map((p) => `address_${prefix}${p}`);

/** The status of `section` for the answers `a` (saved, merged with the prefill, or the firm's read). */
export function onboardingSectionStatus(section: OnboardingClientFormSection, answers: Answers): SectionStatus {
  const a = withLegacyEmployerTitle(answers);
  const person = (s: 'client' | 'spouse') => {
    const required = [`${s}_firstName`, `${s}_lastName`, `${s}_dateOfBirth`, ONBOARDING_IDENTITY_PROVIDED_KEYS[s]];
    const any = [...required, `${s}_middleName`, `${s}_employer`, `${s}_title`, `${s}_phone`, `${s}_email`];
    return fromRequired(a, required, any);
  };
  switch (section) {
    case 'client':
      return person('client');
    case 'spouse':
      if (a['spouse_included'] === false) return 'complete';
      if (a['spouse_included'] !== true) return 'empty';
      return person('spouse') === 'complete' ? 'complete' : 'partial';
    case 'address': {
      const required = [...address('legal')];
      if (a['address_mailingSameAsLegal'] !== true) required.push(...address('mailing'));
      if (a['spouse_included'] === true && a['address_spouseSameAsClient'] === false) required.push(...address('spouseLegal'));
      const any = [...address('legal'), ...address('mailing'), ...address('spouseLegal'),
        'address_mailingSameAsLegal', 'address_spouseSameAsClient'];
      return fromRequired(a, required, any);
    }
    case 'trustedContact': {
      // The trusted contact is whoever `resolveTrustedContact` says (#1954): a family member
      // chosen on the form, or the free-text fields. Complete when there is a name and a way to
      // reach them; anything less that has some answer is in progress.
      const r = resolveTrustedContact(a);
      if (!r) return 'empty';
      return r.name && (r.phone || r.email) ? 'complete' : 'partial';
    }
    case 'children':
      return has(a, 'children_names') || has(a, 'children_dateOfBirths') ? 'complete' : 'empty';
    case 'beneficiary':
      return fromRequired(a, ['beneficiary_primary'], ['beneficiary_primary', 'beneficiary_contingent', 'beneficiary_notes']);
    case 'about':
      return Object.keys(a).some((k) => k.startsWith('about_') && has(a, k)) ? 'complete' : 'empty';
  }
}
